import { useState, useRef } from 'react'
import { useAuth } from '@/context/AuthContext'
import { api } from '@/lib/api'
import { resolveImg } from '@/lib/helpers'

export default function Account() {
  const { user, setUser } = useAuth()
  const fileRef = useRef<HTMLInputElement>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [form, setForm] = useState({
    lastName: user?.fullName?.split(' ')[0] || '',
    firstName: user?.fullName?.split(' ').slice(1).join(' ') || '',
    email: user?.email || '',
    phone: user?.phone || '',
    bio: user?.bio || '',
  })
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar || '')

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Preview
    setAvatarPreview(URL.createObjectURL(file))

    try {
      const res = await api.upload.avatar(file)
      setAvatarPreview(res.url)
    } catch (err: any) {
      setError(err.message || 'Зураг хуулахад алдаа гарлаа')
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')
    setSuccess('')

    try {
      const fullName = `${form.lastName} ${form.firstName}`.trim()
      const updated = await api.users.updateMe({
        fullName,
        phone: form.phone,
        bio: form.bio,
        ...(avatarPreview && avatarPreview !== user?.avatar ? { avatar: avatarPreview } : {}),
      })
      setUser(updated)
      setSuccess('Амжилттай хадгаллаа')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err: any) {
      setError(err.message || 'Хадгалахад алдаа гарлаа')
    } finally {
      setSaving(false)
    }
  }

  const avatarSrc = avatarPreview ? resolveImg(avatarPreview) : undefined

  return (
    <div className="bg-white rounded-2xl md:rounded-3xl border border-gray-200 p-5 sm:p-7 md:p-9">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 md:mb-9">
        <h2 className="text-base md:text-lg font-bold text-gray-800">Хэрэглэгчийн мэдээлэл</h2>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-primary-600 text-white rounded-full px-4 md:px-[18px] py-2 md:py-2.5 text-sm md:text-base font-semibold shadow-xs hover:bg-primary-700 transition disabled:opacity-50 cursor-pointer"
        >
          {saving ? 'Хадгалж байна...' : 'Хадгалах'}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-error-500 rounded-xl text-sm">{error}</div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 text-success-500 rounded-xl text-sm">{success}</div>
      )}

      <div className="flex flex-col gap-5 md:gap-6">
        {/* Avatar section */}
        <div className="flex items-center gap-4 md:gap-6">
          <div className="size-20 md:size-[108px] rounded-2xl overflow-hidden shadow-sm shrink-0 bg-gray-200">
            {avatarSrc ? (
              <img src={avatarSrc} alt="Avatar" className="size-full object-cover" />
            ) : (
              <div className="size-full bg-gray-200 flex items-center justify-center text-gray-400 text-3xl md:text-4xl">
                {form.firstName?.[0] || '?'}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleAvatarChange}
            />
            <button
              onClick={() => fileRef.current?.click()}
              className="bg-white border border-gray-300 rounded-full px-4 md:px-[18px] py-2 md:py-2.5 text-sm md:text-base font-semibold text-gray-700 shadow-xs hover:bg-gray-50 transition cursor-pointer"
            >
              Зураг солих
            </button>
            <p className="text-xs font-medium text-gray-400">JPG, PNG, хэмжээ 5MB.</p>
          </div>
        </div>

        {/* Divider */}
        <hr className="border-gray-200" />

        {/* Row 1: Овог, Нэр */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Овог</label>
            <div className="bg-gray-25 border border-gray-200 rounded-full px-3.5 py-2.5 shadow-xs">
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                className="w-full outline-none text-sm md:text-base text-gray-800 bg-transparent"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Нэр</label>
            <div className="bg-gray-25 border border-gray-200 rounded-full px-3.5 py-2.5 shadow-xs">
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                className="w-full outline-none text-sm md:text-base text-gray-800 bg-transparent"
              />
            </div>
          </div>
        </div>

        {/* Row 2: Цахим шуудан, Утас */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Цахим шуудан</label>
            <div className="bg-gray-25 border border-gray-200 rounded-full px-3.5 py-2.5 shadow-xs">
              <input
                type="email"
                value={form.email}
                disabled
                className="w-full outline-none text-sm md:text-base text-gray-800 bg-transparent opacity-60"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">Утасны дугаар</label>
            <div className="bg-gray-25 border border-gray-200 rounded-full px-3.5 py-2.5 shadow-xs">
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="w-full outline-none text-sm md:text-base text-gray-800 bg-transparent"
              />
            </div>
          </div>
        </div>

        {/* Bio */}
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-gray-700">Био / Танилцуулга</label>
          <div className="bg-gray-25 border border-gray-200 rounded-2xl px-3.5 py-2.5 shadow-xs">
            <textarea
              rows={4}
              value={form.bio}
              onChange={(e) => handleChange('bio', e.target.value)}
              className="w-full outline-none text-sm md:text-base text-gray-800 bg-transparent resize-none"
            />
          </div>
        </div>
      </div>
    </div>
  )
}
